import { Component, DestroyRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { ProfileDateFilterStateService } from '@core/services/profile-date-filter-state.service';
import { CtaAnalyticsDialogComponent } from '@cta-analytics-dialog/cta-analytics-dialog.component';
import { ICtaAnalyticsDialogData } from '@cta-analytics-dialog/cta-analytics-dialog.interface';
import { getCtaAnalyticsDialogConfig } from '@cta-analytics-dialog/cta-analytics-dialog.util';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventDeeplinkType, EEventType, EEventViewMode } from '@enums/event-tracking.enum';
import { IMatchedKpiAnalyticsApi } from '@interfaces/campaign-analytics-api.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreatorCampaignExport, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { ContentContainerComponent } from '@shared/components/content-container/content-container.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IInfoDialogData, InfoDialogComponent } from '@shared/components/info-dialog/info-dialog.component';
import {
  EDateRangeType,
  IDateRangeBounds,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { CampaignDetailsPlatformFeaturesService } from '@shared/components/profile-platform-feature-filter/profile-platform-feature-filter.service';
import { ITileConfiguration } from '@shared/components/social-content-tile/social-content-tile.interface';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { RouterUtilitiesService } from '@utilities/router-utilities.service';
import { endOfDay, startOfDay } from 'date-fns';
import { isEqual } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { timer } from 'rxjs';
import { CampaignDetailsBannerComponent } from './campaign-details-banner/campaign-details-banner.component';
import { CampaignDetailsCreatorOverviewComponent } from './campaign-details-creator-overview/campaign-details-creator-overview.component';
import { CampaignDetailsCreatorTimelineComponent } from './campaign-details-creator-timeline/campaign-details-creator-timeline.component';
import { CampaignDetailsCreatorsComponent } from './campaign-details-creators/campaign-details-creators.component';
import {
  IDeepLinkContentsFilter,
  IDeepLinkCreatorInfo,
  IDeepLinkDeliverablesFilter,
  IDeepLinkGroupInfo,
  TDeepLinkEvent,
} from './campaign-details-deep-link/campaign-details-deep-link.interface';
import { CampaignDetailsDeepLinkService } from './campaign-details-deep-link/campaign-details-deep-link.service';
import { CampaignDetailsDeliverablesComponent } from './campaign-details-deliverables/campaign-details-deliverables.component';
import {
  ICampaignDetailsDeliverablesGroupedParentRow,
  ICampaignDetailsDeliverablesTableRow,
} from './campaign-details-deliverables/campaign-details-deliverables.interface';
import { CampaignDetailsFilterService } from './campaign-details-filter.service';
import { CampaignDetailsFiltersComponent } from './campaign-details-filters/campaign-details-filters.component';
import { CampaignDetailsGoalsComponent } from './campaign-details-goals/campaign-details-goals.component';
import { IGoalsAnalyticsRequest, IGoalsDeepLink } from './campaign-details-goals/campaign-details-goals.interface';
import { CampaignDetailsKpisComponent } from './campaign-details-kpis/campaign-details-kpis.component';
import { CampaignDetailsObservationsComponent } from './campaign-details-observations/campaign-details-observations.component';
import { ICampaignDetailsObservationsTableRow } from './campaign-details-observations/campaign-details-observations.interface';
import { CampaignDetailsOverviewComponent } from './campaign-details-overview/campaign-details-overview.component';
import { CampaignDetailsUndesignatedExecutionOrderService } from './campaign-details-undesignated-execution-order.service';
import {
  CAMPAIGN_DETAILS_ALL_TABS,
  ECampaignDetailsTab,
  IAnalyticsFilterChangedEvent,
  ICampaignAnalyticsFilter,
} from './campaign-details.interface';
import { CampaignDetailsService } from './campaign-details.service';
import { CampaignDetailsStore, ICampaignDetailsAnalyticsFetchParams, REFRESH_INTERVAL } from './campaign-details.store';
import { ICampaignExportConfig } from './campaign-export-config/campaign-export-config.interface';
import { CampaignExportConfigService } from './campaign-export-config/campaign-export-config.service';
import { SocialContentTilesListComponent } from './social-content-tiles-list/social-content-tiles-list.component';

@Component({
  imports: [
    CampaignDetailsBannerComponent,
    CampaignDetailsCreatorOverviewComponent,
    CampaignDetailsCreatorTimelineComponent,
    CampaignDetailsCreatorsComponent,
    CampaignDetailsDeliverablesComponent,
    CampaignDetailsFiltersComponent,
    CampaignDetailsGoalsComponent,
    CampaignDetailsKpisComponent,
    CampaignDetailsObservationsComponent,
    CampaignDetailsOverviewComponent,
    ContentContainerComponent,
    MatButtonModule,
    MatTabsModule,
    RouterModule,
    SocialContentTilesListComponent,
    TranslateModule,
    DotsSpinnerComponent,
    NgxSkeletonLoaderModule,
  ],
  providers: [CampaignDetailsStore, CampaignDetailsDeepLinkService],
  selector: 'app-campaign-details',
  templateUrl: './campaign-details.component.html',
})
export class CampaignDetailsComponent {
  private readonly localDateFilterState = signal<IProfileDateFilterState | null>(null);
  public readonly campaignAnalyticsFilter = signal<ICampaignAnalyticsFilter>({
    creatorIds: new Set(),
    ctaTemplateIds: new Set(),
    endDate: null,
    executionStatuses: new Set(),
    platformFeatures: new Set(),
    startDate: null,
  });

  public readonly creatorCampaignExports = signal<Map<string, ICreatorCampaignExport>>(new Map());
  public readonly creatorBriefingMode = signal<boolean>(false);
  public readonly creatorMode = signal<boolean>(false);
  public readonly exportMode = signal<boolean>(false);
  public readonly ECampaignDetailsTab = ECampaignDetailsTab;
  public readonly activeTab = signal<ECampaignDetailsTab>(ECampaignDetailsTab.overview);
  public readonly selectPlannedTrigger = signal<number>(0);

  public readonly deepLinkContentsFilters = signal<IDeepLinkContentsFilter | null>(null);
  public readonly deepLinkDeliverablesFilters = signal<IDeepLinkDeliverablesFilter | null>(null);
  public readonly deepLinkCreatorContents = signal<IDeepLinkCreatorInfo | null>(null);
  public readonly deepLinkGroupContents = signal<IDeepLinkGroupInfo | null>(null);
  public readonly deepLinkCreatorDeliverables = signal<IDeepLinkCreatorInfo | null>(null);
  public readonly goalsDeepLink = signal<IGoalsDeepLink | null>(null);

  public readonly visibleTabs = computed<ECampaignDetailsTab[]>(() => {
    if (this.creatorBriefingMode()) {
      return [ECampaignDetailsTab.creatorOverview, ECampaignDetailsTab.creatorTimeline];
    }

    const allTabs = CAMPAIGN_DETAILS_ALL_TABS;

    if (!this.exportMode()) {
      return allTabs;
    }

    const campaignExport = this.store.campaignExport$();

    if (!campaignExport || campaignExport.allTabsSelected !== false || campaignExport.tabs === null) {
      return allTabs;
    }

    const enabledTabs = new Set(campaignExport.tabs.map((t) => t.tab));

    return allTabs.filter((tab) => enabledTabs.has(tab));
  });

  public readonly activeTabIndex = computed<number>(() => {
    const idx = this.visibleTabs().indexOf(this.activeTab());

    return idx >= 0 ? idx : 0;
  });

  public readonly exportDataLoading = computed<boolean>(() => this.exportMode() && !this.store.campaignExport$());

  public readonly campaignExportConfig = computed<ICampaignExportConfig>(() =>
    this.campaignExportConfigService.calculateExportConfig(this.exportMode(), this.store.campaignExport$()),
  );

  public readonly activeTabLoading = computed<boolean>(() => {
    if (this.store.loading$() && !this.store.campaignApi$()) {
      return true;
    }

    switch (this.activeTab()) {
      case ECampaignDetailsTab.overview:
        return !this.store.ctaKpiStatusState$().data || this.store.ctaKpiStatusState$().inFlight;
      case ECampaignDetailsTab.performance:
        return this.kpiLoading();
      case ECampaignDetailsTab.deliverables:
        return !this.store.deliverablesState$().data || this.store.deliverablesState$().inFlight;
      case ECampaignDetailsTab.observations:
        return !this.store.deliverablesState$().data || this.store.deliverablesState$().inFlight;
      case ECampaignDetailsTab.kpiGoals:
        return !this.store.deliverablesState$().data || this.store.deliverablesState$().inFlight;
      case ECampaignDetailsTab.contents:
        return !this.store.deliverablesState$().data || this.store.deliverablesState$().inFlight;
      case ECampaignDetailsTab.creators:
        return !this.store.ctaKpiStatusState$().data || this.store.ctaKpiStatusState$().inFlight;
      default:
        return false;
    }
  });
  public readonly filtersLoading = computed<boolean>(
    () => !this.store.initialAnalyticsLoaded$() || !this.store.initialCampaignLoaded$(),
  );

  // In exportMode (the public, shared export view) the selectable dates are bounded by whatever
  // range the exporter permitted: their saved start/end, or the full campaign span if they chose
  // "Full Campaign Duration" (start/end both null).
  public readonly exportDateRangeBounds = computed<IDateRangeBounds | null>(() => {
    if (!this.exportMode()) {
      return null;
    }

    const campaign = this.store.campaignApi$();

    if (!campaign) {
      return null;
    }

    const campaignExport = this.store.campaignExport$();

    if (campaignExport?.start && campaignExport?.end) {
      return { end: new Date(campaignExport.end), start: new Date(campaignExport.start) };
    }

    return { end: campaign.end as Date, start: campaign.start as Date };
  });

  // Derived (not effect-driven) so it's always synchronously in sync with dateFilterReady below -
  // an effect() that .set() this after the fact could flush after dateFilterReady already flipped
  // true and the picker had mounted, leaving it stuck on its own internal default (see
  // applyExportModeDateFilterState, which now only tracks selectedDateRange for fetch purposes).
  public readonly dateFilterState = computed<IProfileDateFilterState | null>(() => {
    if (!this.exportMode()) {
      return this.localDateFilterState();
    }

    const campaignExport = this.store.campaignExport$();

    if (!campaignExport?.start || !campaignExport?.end) {
      return null;
    }

    return {
      endDate: new Date(campaignExport.end),
      selectedRange: EDateRangeType.custom,
      startDate: new Date(campaignExport.start),
    };
  });

  // The date filter must not mount until the export permission (if any) is known, otherwise its
  // one-time initial-state application would run before dateFilterState above reflects it.
  public readonly dateFilterReady = computed<boolean>(() => !this.exportMode() || !!this.store.campaignExport$());

  public readonly deliverablesAnalyticsNodes = computed(() => {
    const allAnalyticsNodes = this.store.deliverablesState$().data;

    if (!allAnalyticsNodes) {
      return allAnalyticsNodes;
    }

    const filteredNodes = this.campaignDetailsFilterService.filterShellPlannedAnalyticsNodes(
      allAnalyticsNodes,
      this.campaignAnalyticsFilter(),
    );

    return this.undesignatedExecutionOrderService.enrichAnalyticsNodesWithUndesignatedOrders(
      filteredNodes,
      allAnalyticsNodes,
    );
  });

  // Feeds the briefing overview's "Optional" section - see getNoExpectationsAnalyticsNodes.
  public readonly optionalAnalyticsNodes = computed(() => {
    const campaignAnalyticsShell = this.store.campaignAnalyticsShell$();

    if (!campaignAnalyticsShell) {
      return null;
    }

    return this.campaignDetailsFilterService.filterShellPlannedAnalyticsNodes(
      this.campaignAnalyticsUtilities.getNoExpectationsAnalyticsNodes(campaignAnalyticsShell),
      this.campaignAnalyticsFilter(),
    );
  });

  public readonly kpiPlatformFeatures = computed<EPlatformFeature[]>(() => {
    const selectedPlatformFeatures = this.campaignAnalyticsFilter().platformFeatures;

    if (selectedPlatformFeatures.size > 0) {
      return Array.from(selectedPlatformFeatures);
    }

    const campaignAnalyticsShellNodes = this.store.campaignAnalyticsShellNodes$();

    if (campaignAnalyticsShellNodes?.length) {
      return Array.from(
        this.campaignDetailsPlatformFeaturesService.getExistingPlatformFeatures(campaignAnalyticsShellNodes),
      );
    }

    const campaignAnalyticsShell = this.store.campaignAnalyticsShell$();

    if (!campaignAnalyticsShell) {
      return [];
    }

    return Array.from(this.campaignDetailsPlatformFeaturesService.getCampaignPlatformFeatures(campaignAnalyticsShell));
  });

  public readonly matchedKpis = computed<IMatchedKpiAnalyticsApi[]>(() => this.store.kpiState$().data ?? []);

  public readonly kpiLoading = computed<boolean>(() => {
    const state = this.store.kpiState$();
    const filterSignature = this.getCurrentFilterSignature();

    return !state.data || state.inFlight || state.filterSignature !== filterSignature;
  });

  public readonly adminMode: boolean;

  public breadcrumbs: IBreadcrumb[] = [];
  public campaignId: string;

  public readonly store = inject(CampaignDetailsStore);
  private readonly deepLinkService = inject(CampaignDetailsDeepLinkService);
  private readonly route = inject(ActivatedRoute);
  private readonly routerUtilities = inject(RouterUtilitiesService);
  private readonly dialog = inject(MatDialog);
  private readonly campaignDetailsFilterService = inject(CampaignDetailsFilterService);
  private readonly undesignatedExecutionOrderService = inject(CampaignDetailsUndesignatedExecutionOrderService);
  private readonly campaignDetailsService = inject(CampaignDetailsService);
  private readonly router = inject(Router);
  private readonly profileDateFilterStateService = inject(ProfileDateFilterStateService);
  private readonly campaignDetailsPlatformFeaturesService = inject(CampaignDetailsPlatformFeaturesService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly campaignExportConfigService = inject(CampaignExportConfigService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly campaignAnalyticsUtilities = inject(CampaignAnalyticsUtilitiesService);

  // Read directly to get each tab's current groupByContent state when opening the CTA dialog from
  // a row click - these tabs' groupByContent signals are otherwise local, not exposed as inputs.
  private readonly deliverablesComponent = viewChild(CampaignDetailsDeliverablesComponent);
  private readonly observationsComponent = viewChild(CampaignDetailsObservationsComponent);

  private routerState: Record<string, unknown> | undefined;
  private creatorName: string;
  private briefingId: string;
  private exportId: string;
  /**
   * Preselects the Scope section's own Creator filter (see CampaignDetailsFiltersComponent's
   * app-profile-general-filters / setInitialFilters below) when arriving via a "view this
   * creator's campaign" link (creator-details' Deliverables tab campaign-column click) - a plain
   * ?creatorId= query param, not router navigation `state`, because that link always opens in a
   * brand new tab (WindowRedirectService.openUrlInNewTab), and `state` can't cross tabs the way it
   * can an in-app this.router.navigate(). Read once here, same one-shot semantics as every other
   * field in this constructor - and immediately stripped back out of the address bar by
   * stripInitialScopeCreatorIdFromUrl() below, so this is genuinely one-shot: the Scope filter
   * itself is never persistent (it's a plain in-memory signal, same as every other Scope filter
   * here), and a refresh of this same tab must land unfiltered rather than silently re-applying
   * whatever creator this link originally carried.
   */
  private initialScopeCreatorId: string | null;
  private selectedDateRange = EDateRangeType.allTime;
  // Value selectedDateRange had as of the last *processed* scope filter change, so diffScopeFilter
  // can report which range type (predefined or custom) was previously active for #20. Synced at
  // the end of onCampaignAnalyticsFilterChanged (not inside onSelectedRangeChanged) so repeated
  // custom-to-custom edits - which don't re-fire onSelectedRangeChanged - still report "custom" as
  // the previous type instead of a stale predefined value.
  private previousSelectedDateRange = EDateRangeType.allTime;
  private lastInitializedCampaignId: string | null = null;
  private lastTokenUsageCheckedCampaignId: string | null = null;
  private tokenUsageWarningDialogRef: MatDialogRef<InfoDialogComponent> | null = null;

  constructor() {
    this.routerState = this.routerUtilities.getRouterState();
    this.adminMode = this.route.snapshot.data['adminMode'] === true;
    this.creatorMode.set(!!(this.routerState && this.routerState['creatorMode']));
    this.creatorName = (this.routerState && (this.routerState['creatorName'] as string)) ?? '';
    this.creatorBriefingMode.set(this.route.snapshot.data['creatorBriefingMode'] === true);
    this.briefingId = this.route.snapshot.params['briefingId'];
    this.campaignId = this.route.snapshot.params['id'] as string;
    this.exportId = this.route.snapshot.params['exportId'];
    this.initialScopeCreatorId = this.route.snapshot.queryParamMap.get('creatorId')?.trim() || null;
    this.stripInitialScopeCreatorIdFromUrl();
    this.destroyRef.onDestroy(() => this.eventTrackingService.setCampaignContext(null));
    this.exportMode.set(this.route.snapshot.data['exportMode'] === true);
    this.initializeDateFilterState();
    this.setInitialFilters();

    if (this.exportMode()) {
      this.store.fetchCampaignExport(this.exportId);
    }

    const campaignFetchParams: ICampaignDetailsAnalyticsFetchParams = {
      adminMode: this.adminMode,
      briefingId: this.briefingId,
      campaignId: this.campaignId,
      creatorBriefingMode: this.creatorBriefingMode(),
      exportId: this.exportId,
      exportMode: this.exportMode(),
    };

    this.store.initializeCampaignDetails(campaignFetchParams);

    this.breadcrumbs = this.campaignDetailsService.getBreadcrumbs(
      this.adminMode,
      this.creatorMode(),
      this.creatorName,
      this.exportMode(),
    );

    this.initialCampaignEffect();
    this.campaignNotFoundEffect();
    this.campaignTokenUsageWarningEffect();
    this.syncActiveTabToVisibleTabs();

    timer(REFRESH_INTERVAL, REFRESH_INTERVAL)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkAndShowCampaignTokenUsageWarning();
      });
  }

  public onTileClick(tileConfiguration: ITileConfiguration): void {
    // Optional (no_expectations) tiles come from their own nodes, kept out of the main list.
    const analyticsNode =
      this.store.deliverablesState$().data?.find((node) => node.id === tileConfiguration.id) ??
      this.optionalAnalyticsNodes()?.find((node) => node.id === tileConfiguration.id);

    if (!analyticsNode || !this.store.campaignAnalyticsShell$() || !this.store.campaignApi$()) {
      return;
    }

    const dialogData: ICtaAnalyticsDialogData = {
      adminMode: this.adminMode,
      analyticsNode,
      briefingId: this.briefingId,
      campaign: this.store.campaignApi$()!,
      campaignAnalytics: this.store.campaignAnalyticsShell$()!,
      campaignExport: this.store.campaignExport$()!,
      creatorBriefingMode: this.creatorBriefingMode(),
      currentTab: toEventFrontendTab(
        this.creatorBriefingMode() ? ECampaignDetailsTab.creatorOverview : ECampaignDetailsTab.contents,
      ),
      exportMode: this.exportMode(),
      selectedCtaId: tileConfiguration.selectedCtaId ?? null,
    };

    this.openCtaAnalyticsDialog(dialogData);
  }

  public onDeliverableRowClick(row: ICampaignDetailsDeliverablesTableRow): void {
    const config: ICampaignExportConfig = this.campaignExportConfig();

    if (!config.allowDeliverablesRowClick) {
      return;
    }

    const analyticsNode = this.store.deliverablesState$().data?.find((node) => node.id === row.analyticsNodeId);

    if (!analyticsNode || !this.store.campaignAnalyticsShell$() || !this.store.campaignApi$()) {
      return;
    }

    const dialogData: ICtaAnalyticsDialogData = {
      adminMode: this.adminMode,
      analyticsNode,
      briefingId: this.briefingId,
      campaign: this.store.campaignApi$()!,
      campaignAnalytics: this.store.campaignAnalyticsShell$()!,
      campaignExport: this.store.campaignExport$()!,
      creatorBriefingMode: this.creatorBriefingMode(),
      currentTab: toEventFrontendTab(ECampaignDetailsTab.deliverables),
      exportMode: this.exportMode(),
      selectedCtaId: row.ctaTemplateId,
      selectedDeliverableId: row.deliverableId,
      selectedDeliverableType: row.deliverableType,
      viewMode: this.toViewMode(this.deliverablesComponent()?.groupByContent()),
    };

    this.openCtaAnalyticsDialog(dialogData);
  }

  public onObservationRowClick(row: ICampaignDetailsObservationsTableRow): void {
    const config: ICampaignExportConfig = this.campaignExportConfig();

    if (!config.allowObservationsRowClick) {
      return;
    }

    const analyticsNode = this.store.deliverablesState$().data?.find((node) => node.id === row.analyticsNodeId);

    if (!analyticsNode || !this.store.campaignAnalyticsShell$() || !this.store.campaignApi$()) {
      return;
    }

    const dialogData: ICtaAnalyticsDialogData = {
      adminMode: this.adminMode,
      analyticsNode,
      briefingId: this.briefingId,
      campaign: this.store.campaignApi$()!,
      campaignAnalytics: this.store.campaignAnalyticsShell$()!,
      campaignExport: this.store.campaignExport$()!,
      creatorBriefingMode: this.creatorBriefingMode(),
      currentTab: toEventFrontendTab(ECampaignDetailsTab.observations),
      exportMode: this.exportMode(),
      selectedCtaId: row.ctaTemplateId,
      selectedDeliverableId: row.deliverableId,
      selectedDeliverableType: row.deliverableType,
      viewMode: this.toViewMode(this.observationsComponent()?.groupByContent()),
    };

    this.openCtaAnalyticsDialog(dialogData);
  }

  public onGoalsAnalyticsRequested(request: IGoalsAnalyticsRequest): void {
    if (!this.store.campaignAnalyticsShell$() || !this.store.campaignApi$()) {
      return;
    }

    const dialogData: ICtaAnalyticsDialogData = {
      adminMode: this.adminMode,
      analyticsNode: request.analyticsNode,
      briefingId: this.briefingId,
      campaign: this.store.campaignApi$()!,
      campaignAnalytics: this.store.campaignAnalyticsShell$()!,
      campaignExport: this.store.campaignExport$()!,
      creatorBriefingMode: this.creatorBriefingMode(),
      currentTab: toEventFrontendTab(ECampaignDetailsTab.kpiGoals),
      expectedKpiId: request.expectedKpiId,
      exportMode: this.exportMode(),
      kpiType: request.kpiType,
      selectedCtaId: request.ctaId,
      selectedDeliverableId: request.deliverableId,
      selectedDeliverableType: request.deliverableType,
      viewMode: undefined,
    };

    this.openCtaAnalyticsDialog(dialogData);
  }

  public onDeliverableRowGroupClick(
    row: Pick<ICampaignDetailsDeliverablesGroupedParentRow, 'analyticsNodeId' | 'ctaTemplateId'>,
  ): void {
    const config: ICampaignExportConfig = this.campaignExportConfig();

    if (!config.allowDeliverableGroupClick) {
      return;
    }

    const analyticsNode = this.store.deliverablesState$().data?.find((node) => node.id === row.analyticsNodeId);

    if (!analyticsNode || !this.store.campaignAnalyticsShell$() || !this.store.campaignApi$()) {
      return;
    }

    const dialogData: ICtaAnalyticsDialogData = {
      adminMode: this.adminMode,
      analyticsNode,
      briefingId: this.briefingId,
      campaign: this.store.campaignApi$()!,
      campaignAnalytics: this.store.campaignAnalyticsShell$()!,
      campaignExport: this.store.campaignExport$()!,
      creatorBriefingMode: this.creatorBriefingMode(),
      currentTab: toEventFrontendTab(ECampaignDetailsTab.deliverables),
      exportMode: this.exportMode(),
      selectedCtaId: row.ctaTemplateId,
      viewMode: this.toViewMode(this.deliverablesComponent()?.groupByContent()),
    };

    this.openCtaAnalyticsDialog(dialogData);
  }

  private openCtaAnalyticsDialog(dialogData: ICtaAnalyticsDialogData): void {
    const dialogRef = this.dialog.open(CtaAnalyticsDialogComponent, getCtaAnalyticsDialogConfig(dialogData));

    dialogRef.afterClosed().subscribe((result: ITileConfiguration | undefined) => {
      if (result) {
        this.onTileClick(result);
      }
    });
  }

  private toViewMode(groupByContent: boolean | undefined): EEventViewMode | undefined {
    if (groupByContent === undefined) {
      return undefined;
    }

    return groupByContent ? EEventViewMode.groupedByContent : EEventViewMode.list;
  }

  public onDateRangeChanged(dateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>): void {
    this.profileDateFilterStateService.persistState({
      endDate: dateRange.endDate,
      entityId: this.campaignId,
      entityType: 'campaign',
      selectedRange: this.selectedDateRange,
      startDate: dateRange.startDate,
      userId: AuthService.currentDbUser?.id ?? null,
    });
  }

  public onCampaignAnalyticsFilterChanged(campaignAnalyticsFilter: ICampaignAnalyticsFilter): void {
    const previousFilter = this.campaignAnalyticsFilter();

    this.campaignAnalyticsFilter.set(campaignAnalyticsFilter);

    const { newValue, previousValue, source } = this.diffScopeFilter(previousFilter, campaignAnalyticsFilter);

    // Synced here (after processing), not inside onSelectedRangeChanged - selectedDateRange only
    // changes type via onSelectedRangeChanged, which always fires before this handler for the same
    // user action, so it already reflects the type this change produced. Custom-to-custom edits
    // never re-fire onSelectedRangeChanged (the type doesn't change), so syncing on every processed
    // filter change - rather than only on type transitions - is what keeps previousSelectedDateRange
    // reporting "custom" instead of a stale predefined value across repeated custom edits.
    this.previousSelectedDateRange = this.selectedDateRange;

    // profile-date-filter's pickers can re-fire dateRangeChanged for the same dates (its underlying
    // Material date pickers briefly reset and re-complete an already-set range when re-synced after
    // opening/closing), reaching here as a second call with nothing actually different.
    // diffScopeFilter returns no source in that case - skip both tracking and refetching data for a
    // no-op filter change (store.updateFilter triggers a real analytics refetch).
    if (!source) {
      return;
    }

    this.eventTrackingService.track(EEventType.campaignAnalyticsScopeChanged, {
      campaignId: this.campaignId,
      newValue,
      previousValue,
      scopeCreatorsFilterActive: campaignAnalyticsFilter.creatorIds.size > 0,
      scopeDurationFilterActive: this.selectedDateRange !== EDateRangeType.allTime,
      scopePlatformsFilterActive: campaignAnalyticsFilter.platformFeatures.size > 0,
      source,
    });

    if (!this.store.campaignApi$()) {
      return;
    }

    this.store.updateFilter(campaignAnalyticsFilter);
  }

  private diffScopeFilter(
    previous: ICampaignAnalyticsFilter,
    current: ICampaignAnalyticsFilter,
  ): { newValue: string | undefined; previousValue: string | undefined; source: string | undefined } {
    if (!isEqual(previous.platformFeatures, current.platformFeatures)) {
      return {
        newValue: Array.from(current.platformFeatures).join(','),
        previousValue: Array.from(previous.platformFeatures).join(','),
        source: 'platform_features',
      };
    }

    if (!isEqual(previous.creatorIds, current.creatorIds)) {
      return {
        newValue: Array.from(current.creatorIds).join(','),
        previousValue: Array.from(previous.creatorIds).join(','),
        source: 'creator_ids',
      };
    }

    if (
      previous.startDate?.getTime() !== current.startDate?.getTime() ||
      previous.endDate?.getTime() !== current.endDate?.getTime()
    ) {
      // A predefined range (thisWeek, last30Days, etc.) reports its enum value - the actual dates
      // are an implementation detail of that preset, not what changed. Only "custom" reports the
      // actual start/end dates, since that's the one case where the dates themselves are the choice.
      return {
        newValue:
          this.selectedDateRange === EDateRangeType.custom
            ? CampaignDetailsComponent.formatDateRange(current.startDate, current.endDate)
            : this.selectedDateRange,
        previousValue:
          this.previousSelectedDateRange === EDateRangeType.custom
            ? CampaignDetailsComponent.formatDateRange(previous.startDate, previous.endDate)
            : this.previousSelectedDateRange,
        source: 'duration',
      };
    }

    return { newValue: undefined, previousValue: undefined, source: undefined };
  }

  // Comma matches the delimiter already used elsewhere for multi-value fields (e.g. platform
  // features/creator ids joined with ','), so start/end stay consistent with that convention.
  private static formatDateRange(startDate: Date | null, endDate: Date | null): string {
    return `${startDate?.toISOString() ?? ''},${endDate?.toISOString() ?? ''}`;
  }

  public onAnalyticsFilterChanged(event: IAnalyticsFilterChangedEvent, tab: ECampaignDetailsTab): void {
    this.eventTrackingService.track(EEventType.campaignAnalyticsFilterChanged, {
      campaignId: this.campaignId,
      currentFilter: event.currentFilter,
      currentFilterValue: event.currentFilterValue,
      currentTab: toEventFrontendTab(tab),
      newValue: event.currentFilterValue,
      previousValue: event.previousFilterValue,
      source: event.currentFilter,
    });
  }

  public onSelectedRangeChanged(selectedRange: EDateRangeType): void {
    this.selectedDateRange = selectedRange;
  }

  public onSelectedTabIndexChange(index: number): void {
    const nextTab = this.visibleTabs()[index];

    if (!nextTab || nextTab === this.activeTab()) {
      return;
    }

    this.eventTrackingService.track(EEventType.campaignAnalyticsTabOpened, {
      campaignId: this.campaignId,
      currentTab: toEventFrontendTab(nextTab),
      previousTab: toEventFrontendTab(this.activeTab()),
    });

    this.activeTab.set(nextTab);
    this.store.selectTabForFetch(nextTab);
  }

  public onOverviewDeepLinkClicked(event: TDeepLinkEvent): void {
    const revision = Date.now();

    // A group link's Contents filters read that group's results; without the group they could hide every content.
    if (this.deepLinkGroupContents() && event.tab !== ECampaignDetailsTab.contents) {
      this.deepLinkContentsFilters.set(this.deepLinkService.buildContentsFilter(revision, new Set()));
    }

    this.deepLinkCreatorContents.set(null);
    this.deepLinkGroupContents.set(null);
    this.deepLinkCreatorDeliverables.set(null);

    switch (event.tab) {
      case ECampaignDetailsTab.contents:
        this.deepLinkContentsFilters.set(
          this.deepLinkService.buildContentsFilter(revision, event.matchingTypes, event.contentStatuses),
        );
        break;

      case ECampaignDetailsTab.deliverables:
        this.deepLinkDeliverablesFilters.set(
          this.deepLinkService.buildDeliverablesFilter(revision, event.statuses, event.matchingTypes),
        );
        break;

      case ECampaignDetailsTab.kpiGoals:
        this.goalsDeepLink.set({ creatorId: null, revision });
        break;
    }

    this.trackDeeplinkUsed(event);
    this.activeTab.set(event.tab);
    this.store.selectTabForFetch(event.tab);
  }

  public onCreatorsDeepLinkClicked(event: TDeepLinkEvent): void {
    const revision = Date.now();

    switch (event.tab) {
      case ECampaignDetailsTab.contents:
        this.deepLinkCreatorContents.set(event.creator ?? null);
        this.deepLinkGroupContents.set(event.group ?? null);
        this.deepLinkContentsFilters.set(
          this.deepLinkService.buildContentsFilter(revision, event.matchingTypes, event.contentStatuses),
        );
        break;

      case ECampaignDetailsTab.deliverables:
        this.deepLinkCreatorDeliverables.set(event.creator ?? null);
        this.deepLinkDeliverablesFilters.set(
          this.deepLinkService.buildDeliverablesFilter(
            revision,
            event.statuses,
            event.matchingTypes,
            event.ctaTemplateIds,
            event.deliverableTypes,
          ),
        );
        break;

      case ECampaignDetailsTab.kpiGoals:
        this.goalsDeepLink.set({ creatorId: event.creator?.id ?? null, revision });
        break;
    }

    this.trackDeeplinkUsed(event);
    this.activeTab.set(event.tab);
    this.store.selectTabForFetch(event.tab);
  }

  private trackDeeplinkUsed(event: TDeepLinkEvent): void {
    this.eventTrackingService.track(EEventType.campaignAnalyticsDeeplinkUsed, {
      campaignId: this.campaignId,
      currentFilterValue: this.buildDeeplinkFilterValue(event),
      currentTab: toEventFrontendTab(event.tab),
      deeplinkType: this.getDeeplinkType(event),
      previousTab: toEventFrontendTab(this.activeTab()),
    });
  }

  private getDeeplinkType(event: TDeepLinkEvent): EEventDeeplinkType {
    switch (event.tab) {
      case ECampaignDetailsTab.deliverables:
        return EEventDeeplinkType.deliverable;
      case ECampaignDetailsTab.kpiGoals:
        return EEventDeeplinkType.kpi;
      case ECampaignDetailsTab.contents:
        return event.matchingTypes?.size ? EEventDeeplinkType.contentType : EEventDeeplinkType.contentStatus;
    }
  }

  private buildDeeplinkFilterValue(event: TDeepLinkEvent): string | undefined {
    const parts: string[] = [];

    switch (event.tab) {
      case ECampaignDetailsTab.contents:
        if (event.matchingTypes?.size) {
          parts.push(`matching:${Array.from(event.matchingTypes).join(',')}`);
        }

        if (event.contentStatuses?.size) {
          parts.push(`contentStatus:${Array.from(event.contentStatuses).join(',')}`);
        }

        if (event.group) {
          parts.push('groups:1');
        }

        break;
      case ECampaignDetailsTab.deliverables:
        if (event.matchingTypes?.size) {
          parts.push(`matching:${Array.from(event.matchingTypes).join(',')}`);
        }

        if (event.statuses?.size) {
          parts.push(`status:${Array.from(event.statuses).join(',')}`);
        }

        if (event.ctaTemplateIds?.size) {
          parts.push(`groups:${event.ctaTemplateIds.size}`);
        }

        if (event.deliverableTypes?.size) {
          parts.push(`deliverableType:${Array.from(event.deliverableTypes).join(',')}`);
        }

        break;
      case ECampaignDetailsTab.kpiGoals:
        if (event.statuses?.size) {
          parts.push(`status:${Array.from(event.statuses).join(',')}`);
        }

        break;
    }

    return parts.length ? parts.join(';') : undefined;
  }

  public onFilteredCreatorRemovedContents(): void {
    this.deepLinkCreatorContents.set(null);
  }

  public onFilteredGroupRemovedContents(): void {
    this.deepLinkGroupContents.set(null);
  }

  public onFilteredCreatorRemovedDeliverables(): void {
    this.deepLinkCreatorDeliverables.set(null);
  }

  public onViewAllPlannedContents(): void {
    const nodes = this.deliverablesAnalyticsNodes();
    const plannedCount = nodes?.filter((n) => n.executionStatus === EMatchingType.planned).length ?? 0;

    this.activeTab.set(ECampaignDetailsTab.creatorTimeline);

    if (plannedCount > 0) {
      this.selectPlannedTrigger.update((v) => v + 1);
    }
  }

  private syncActiveTabToVisibleTabs(): void {
    effect(() => {
      const visible = this.visibleTabs();
      const current = this.activeTab();

      if (visible.length > 0 && !visible.includes(current)) {
        this.activeTab.set(visible[0]);
        this.store.selectTabForFetch(visible[0]);
      }
    });
  }

  private initialCampaignEffect(): void {
    effect(() => {
      const initialCampaignLoaded = this.store.initialCampaignLoaded$();
      const campaign = this.store.campaignApi$();

      if (!initialCampaignLoaded || !campaign) {
        return;
      }

      // In exportMode, wait for the campaign export config to load too - the initial date range
      // needs to respect the range the exporter permitted (see applyExportModeDateFilterState).
      if (this.exportMode() && !this.store.campaignExport$()) {
        return;
      }

      this.setCreatorCampaignExports(campaign);
      this.updateBreadcrumbs(campaign);

      if (this.lastInitializedCampaignId === campaign.id) {
        return;
      }

      this.lastInitializedCampaignId = campaign.id;
      this.campaignId = campaign.id;
      // Safe only because no code path in this app navigates directly from one campaign's :id to
      // another's on this same route (every campaign-to-campaign transition goes through the list
      // page first, which forces a full destroy) - Angular's default RouteReuseStrategy already
      // reuses the component instance for same-route param changes even with no custom strategy,
      // so "no custom RouteReuseStrategy exists" alone would NOT make this safe on its own. If a
      // same-route campaign-to-campaign nav (e.g. "next campaign") is ever added, re-check for
      // stale campaignId leaks here.
      // userGroupId here is a fallback for no-login (briefing/report) events fired from nested
      // components that can't source it from a logged-in user - harmless no-op otherwise.
      this.eventTrackingService.setCampaignContext(campaign.id, campaign.userGroupId ?? null);

      if (this.creatorBriefingMode() || this.exportMode()) {
        // Direct-link entry points (public briefing/report links) never pass through the campaigns
        // list, so #18 fires here instead of campaigns-datatable.component.ts's onRowClick(). No-login
        // views: source user_group_id from the campaign data itself, not the current user.
        this.eventTrackingService.track(EEventType.campaignAnalyticsOpened, {
          campaignId: campaign.id,
          isBriefing: this.creatorBriefingMode(),
          isReport: this.exportMode(),
          source: 'direct_link',
          userGroupId: campaign.userGroupId ?? '',
        });
      }

      if (this.creatorBriefingMode()) {
        // No-login view: source user_group_id from the campaign data itself, not the current user.
        // The briefing is per-creator, so the campaign response's campaignCreators always holds
        // exactly one entry - that's the briefing's creator.
        const briefingCreatorId = campaign.campaignCreators?.[0]?.creatorId;

        this.eventTrackingService.track(EEventType.campaignBriefingOpened, {
          campaignBriefingId: this.briefingId,
          campaignId: campaign.id,
          creatorId: briefingCreatorId ?? undefined,
          isBriefing: true,
          userGroupId: campaign.userGroupId ?? '',
        });
      }

      if (this.exportMode()) {
        this.applyExportModeDateFilterState();
      }

      this.setInitialDateRange();
      this.store.updateFilter(this.campaignAnalyticsFilter());
    });
  }

  private applyExportModeDateFilterState(): void {
    const campaignExport = this.store.campaignExport$();

    // dateFilterState (computed) already derives its value from campaignExport$() - this only
    // needs to keep the plain selectedDateRange field in sync for getInitialDateRange/setInitialDateRange.
    this.selectedDateRange =
      campaignExport?.start && campaignExport?.end ? EDateRangeType.custom : EDateRangeType.allTime;
    // No real "previous" range exists yet at init - seed both so the first tracked duration
    // change reports this restored type, not the field's plain default.
    this.previousSelectedDateRange = this.selectedDateRange;
  }

  private setCreatorCampaignExports(campaign: TCampaignApi): void {
    const { creatorCampaignExports } = campaign;

    if (!creatorCampaignExports) {
      return;
    }

    this.creatorCampaignExports.set(new Map(creatorCampaignExports.map((cce) => [cce.creatorId, cce])));
  }

  private updateBreadcrumbs(campaign: TCampaignApi): void {
    if (this.breadcrumbs.length > 1) {
      const newBreadcrumbs = [...this.breadcrumbs];

      newBreadcrumbs.splice(1, 1, { label: campaign.name });
      this.breadcrumbs = newBreadcrumbs;
    }
  }

  private campaignNotFoundEffect(): void {
    effect(() => {
      if (this.store.campaignNotFound$()) {
        this.router.navigate(['/campaigns']);
      }
    });
  }

  private getInitialDateRange(): Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'> {
    const campaign = this.store.campaignApi$();
    const campaignStartDate = campaign ? startOfDay(campaign.start as Date) : null;
    const campaignEndDate = campaign ? endOfDay(campaign.end as Date) : null;

    // allTime must always reflect the campaign's current start/end - the campaign's dates can
    // change after the range was persisted, so a cached start/end would filter out new data.
    if (this.selectedDateRange === EDateRangeType.allTime) {
      return {
        endDate: campaignEndDate,
        startDate: campaignStartDate,
      };
    }

    const persistedDateFilterState = this.dateFilterState();

    if (persistedDateFilterState?.startDate && persistedDateFilterState.endDate) {
      return {
        endDate: endOfDay(persistedDateFilterState.endDate),
        startDate: startOfDay(persistedDateFilterState.startDate),
      };
    }

    return {
      endDate: campaignEndDate,
      startDate: campaignStartDate,
    };
  }

  private setInitialDateRange(): void {
    const { endDate, startDate } = this.getInitialDateRange();

    this.campaignAnalyticsFilter.set({
      ...this.campaignAnalyticsFilter(),
      endDate,
      startDate,
    });

    if (this.selectedDateRange === EDateRangeType.allTime) {
      this.profileDateFilterStateService.persistState({
        endDate,
        entityId: this.campaignId,
        entityType: 'campaign',
        selectedRange: this.selectedDateRange,
        startDate,
        userId: AuthService.currentDbUser?.id ?? null,
      });
    }
  }

  /**
   * Rewrites the address bar to drop ?creatorId= right after reading it, using plain
   * window.history.replaceState (same technique LoginComponent.resetUrlState() uses for its own
   * ephemeral navigation flags) rather than this.router.navigate() - a real Router navigation
   * fired from inside this constructor, while the navigation that's still activating this very
   * component hasn't finished, risks landing before that activation completes. This is a cosmetic
   * rewrite only: no navigation, no guards, no re-running this component - so it's safe here.
   * Its only job is making sure the value never resurfaces: a refresh, or copying the link to
   * someone else, hits the now-clean URL and gets the normal unfiltered Scope state.
   */
  private stripInitialScopeCreatorIdFromUrl(): void {
    if (!this.initialScopeCreatorId) {
      return;
    }

    const remainingParams = new URLSearchParams(window.location.search);
    remainingParams.delete('creatorId');

    const remainingQueryString = remainingParams.toString();
    const cleanedUrl = `${window.location.pathname}${remainingQueryString ? `?${remainingQueryString}` : ''}${window.location.hash}`;

    window.history.replaceState(window.history.state, '', cleanedUrl);
  }

  private setInitialFilters(): void {
    const { endDate, startDate } = this.getInitialDateRange();

    this.campaignAnalyticsFilter.set({
      creatorIds: this.initialScopeCreatorId ? new Set([this.initialScopeCreatorId]) : new Set(),
      ctaTemplateIds: new Set(),
      endDate,
      executionStatuses: new Set(),
      platformFeatures: new Set(),
      startDate,
    });
  }

  private initializeDateFilterState(): void {
    // exportMode is a public, shared view of one specific export - it must never pick up a
    // per-user local preference (e.g. left over from browsing the normal campaign details page
    // as the campaign owner). dateFilterState (computed) derives the real value from
    // campaignExport$() once it loads; until then this should stay at a neutral default.
    if (this.exportMode()) {
      this.selectedDateRange = EDateRangeType.allTime;
      this.previousSelectedDateRange = this.selectedDateRange;

      return;
    }

    const resolvedState = this.profileDateFilterStateService.resolveState({
      defaultEndDate: null,
      defaultSelectedRange: EDateRangeType.allTime,
      defaultStartDate: null,
      entityId: this.campaignId,
      entityType: 'campaign',
      hideAllTimeRangeOption: false,
      userId: AuthService.currentDbUser?.id ?? null,
    });
    const resolvedStateUnknown: unknown = resolvedState;

    if (!this.isResolvedDateFilterState(resolvedStateUnknown)) {
      return;
    }

    this.localDateFilterState.set(resolvedStateUnknown.filterState);
    this.selectedDateRange = resolvedStateUnknown.selectedRange;
    this.previousSelectedDateRange = this.selectedDateRange;
  }

  private isResolvedDateFilterState(
    value: unknown,
  ): value is { filterState: IProfileDateFilterState | null; selectedRange: EDateRangeType } {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return 'filterState' in value && 'selectedRange' in value;
  }

  private getCurrentFilterSignature(): string {
    const filter = this.campaignAnalyticsFilter();

    return JSON.stringify({
      creatorIds: Array.from(filter.creatorIds).sort(),
      endDate: filter.endDate?.toISOString() ?? null,
      platformFeatures: Array.from(filter.platformFeatures).sort(),
      startDate: filter.startDate?.toISOString() ?? null,
    });
  }

  private campaignTokenUsageWarningEffect(): void {
    effect(() => {
      const initialCampaignLoaded = this.store.initialCampaignLoaded$();
      const campaign = this.store.campaignApi$();

      if (!initialCampaignLoaded || !campaign) {
        return;
      }

      this.checkAndShowCampaignTokenUsageWarning();
    });
  }

  private checkAndShowCampaignTokenUsageWarning(): void {
    const campaign = this.store.campaignApi$();

    if (!campaign || (campaign.tokenUsage ?? 0) < 1.0) {
      this.lastTokenUsageCheckedCampaignId = null;

      return;
    }

    if (this.lastTokenUsageCheckedCampaignId === campaign.id) {
      return;
    }

    if (this.tokenUsageWarningDialogRef) {
      return;
    }

    this.lastTokenUsageCheckedCampaignId = campaign.id;

    this.tokenUsageWarningDialogRef = this.dialog.open(InfoDialogComponent, {
      data: {
        closeText: this.translate.instant('shared.close'),
        message: this.translate.instant('pages.campaignDetails.tokenUsageWarning.message'),
        title: this.translate.instant('pages.campaignDetails.tokenUsageWarning.title'),
        type: 'warning',
      } satisfies IInfoDialogData,
      width: '400px',
    });

    this.tokenUsageWarningDialogRef.afterClosed().subscribe(() => {
      this.tokenUsageWarningDialogRef = null;
    });
  }
}
